import { getBasicContentSchema } from '@/lib/zy-tiptap-utils';
import { Node, mergeAttributes, type JSONContent } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import MultipleChoiceChoiceNodeView from './multiple-choice-choice-node-view';
import MultipleChoiceNodeView from './multiple-choice-node-view';
import MultipleChoiceQuestionNodeView from './multiple-choice-question-node-view';

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        multipleChoice: {
            insertMultipleChoice: (options?: InsertMultipleChoiceOptions) => ReturnType;
            insertBareMultipleChoice: (options?: InsertMultipleChoiceOptions) => ReturnType;
        };
    }
}

export interface InsertMultipleChoiceOptions {
    instruction?: string;
    question?: string;
    choices?: Array<{
        text: string;
        isCorrect?: boolean;
        explanation?: string;
    }>;
    allowMultiple?: boolean;
    title?: string;
}

const defaultMultipleChoiceNode = (options: InsertMultipleChoiceOptions) => {
    const choices = options.choices || [
        { text: '', isCorrect: true },
        { text: '', isCorrect: false },
        { text: '', isCorrect: false },
    ];

    const choiceNodes = choices.map((choice: any) => ({
        type: 'multipleChoiceChoice',
        attrs: { isCorrect: choice.isCorrect || false },
        content: [
            {
                type: 'paragraph',
                content: choice.text ? [{ type: 'text', text: choice.text }] : undefined,
            },
            {
                type: 'multipleChoiceExplanation',
                content: [
                    {
                        type: 'paragraph',
                        content: choice.explanation ? [{ type: 'text', text: choice.explanation }] : undefined,
                    },
                ],
            },
        ],
    }));

    const multipleChoiceNode = {
        type: 'multipleChoice',
        attrs: {
            title: options.title || 'Multiple Choice Question',
            allowMultiple: options.allowMultiple || false,
        },
        content: [
            {
                type: 'multipleChoiceQuestion',
                content: [
                    {
                        type: 'paragraph',
                        content: options.question ? [{ type: 'text', text: options.question }] : undefined,
                    },
                ],
            },
            ...choiceNodes,
        ],
    };

    return multipleChoiceNode;
};

// Multiple Choice Node Extension
export const MultipleChoiceNodeHeadless = Node.create({
    name: 'multipleChoice',

    group: 'block',

    content: 'multipleChoiceQuestion multipleChoiceChoice+',

    defining: true,

    isolating: true,

    selectable: true,

    draggable: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-mc-id'),
                renderHTML: attributes => ({
                    'data-mc-id': attributes.id,
                }),
            },
            allowMultiple: {
                default: false,
                parseHTML: element => element.getAttribute('data-allow-multiple') === 'true',
                renderHTML: attributes => ({
                    'data-allow-multiple': attributes.allowMultiple ? 'true' : 'false',
                }),
            },
            title: {
                default: '',
                parseHTML: element => element.getAttribute('data-title') || '',
                renderHTML: attributes => ({
                    'data-title': attributes.title,
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="multiple-choice"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'multiple-choice',
                class: 'multiple-choice-container',
            }),
            0,
        ];
    },

    addCommands() {
        return {
            insertMultipleChoice:
                (options: InsertMultipleChoiceOptions = {}) =>
                ({ commands }) => {
                    const multipleChoiceNode = defaultMultipleChoiceNode(options);

                    const instructionNode: JSONContent = {
                        type: 'paragraph',
                        content: options.instruction ? [{ type: 'text', text: options.instruction }] : undefined,
                        attrs: {
                            textAlign: null,
                        },
                    };

                    const questionSet = {
                        type: 'questionSet',
                        attrs: {
                            activityType: 'participation',
                            questionType: 'multipleChoice',
                        },
                        content: [instructionNode, multipleChoiceNode],
                    };

                    return commands.insertContent(questionSet);
                },
            insertBareMultipleChoice:
                (options: InsertMultipleChoiceOptions = {}) =>
                ({ commands }) => {
                    return commands.insertContent(defaultMultipleChoiceNode(options));
                },
        };
    },
});

// Question Node Extension
export const QuestionNodeHeadless = Node.create({
    name: 'multipleChoiceQuestion',

    group: 'multipleChoiceContent',

    content: function () {
        return getBasicContentSchema();
    },

    defining: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-mc-question-id'),
                renderHTML: attributes => ({
                    'data-mc-question-id': attributes.id,
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="mc-question"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'mc-question',
            }),
            0,
        ];
    },
});

// Choice Node Extension
export const ChoiceNodeHeadless = Node.create({
    name: 'multipleChoiceChoice',

    group: 'multipleChoiceContent',

    content: function () {
        return `${getBasicContentSchema()} multipleChoiceExplanation{1}`;
    },

    defining: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-mc-choice-id'),
                renderHTML: attributes => ({
                    'data-mc-choice-id': attributes.id,
                }),
            },
            isCorrect: {
                default: false,
                parseHTML: element => element.getAttribute('data-correct') === 'true',
                renderHTML: attributes => ({
                    'data-correct': attributes.isCorrect ? 'true' : 'false',
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="mc-choice"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'mc-choice',
            }),
            0,
        ];
    },
});

export const ChoiceNode = ChoiceNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(MultipleChoiceChoiceNodeView);
    },
});

// Choice Node Extension
export const ExplanationNodeHeadless = Node.create({
    name: 'multipleChoiceExplanation',

    group: 'multipleChoiceContent',

    content: function () {
        return getBasicContentSchema();
    },

    defining: true,

    addAttributes() {
        return {
            id: {
                default: null,
                parseHTML: element => element.getAttribute('data-mc-explanation-id'),
                renderHTML: attributes => ({
                    'data-mc-explanation-id': attributes.id,
                }),
            },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="mc-explanation"]',
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, {
                'data-type': 'mc-explanation',
            }),
            0,
        ];
    },
});

export const ExplanationNode = ExplanationNodeHeadless.extend({
    addNodeView() {
        return () => {
            const dom = document.createElement('div');
            dom.className = 'border-2 border-dashed border-gray-300 p-1 rounded w-full';
            const content = document.createElement('div');
            dom.append(content);

            return {
                dom,
                contentDOM: content,
            };
        };
    },
});

export const MultipleChoiceNode = MultipleChoiceNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(MultipleChoiceNodeView, { trackNodeViewPosition: true });
    },
});

export const QuestionNode = QuestionNodeHeadless.extend({
    addNodeView() {
        return ReactNodeViewRenderer(MultipleChoiceQuestionNodeView);
    },
});
